import { useEffect, useMemo } from 'react';
import {
    useGetAccountQuery,
    useGetTransactionsInfiniteQuery,
    useGetPendingTransactionsQuery,
} from 'lib/services';
import { getListItems } from '../../../../../utils/transactions';
import { isEmpty } from 'lib/utils';
import {
    AccountBalanceTransactionItem,
    PendingTransactionItem,
    Transaction,
    TransactionListElement,
} from 'lib/type';
const useTransactions = provider => {
    const {
        data: transactionsData,
        isFetching: isFetchingTransactions,
        refetch: refetchTransactions,
        fetchNextPage,
        hasNextPage,
        isLoading: isLoadingTransactions,
    } = useGetTransactionsInfiniteQuery({ provider });

    const {
        data: pendingTransactionsData,
        isFetching: isFetchingPendingTransactions,
        isLoading: isLoadingPendingTransactions,
    } = useGetPendingTransactionsQuery();

    const {
        data: account,
        isFetching: isFetchingAccount,
        isLoading: isLoadingAccount,
        error,
    } = useGetAccountQuery();

    const isFetching =
        isFetchingTransactions ||
        isFetchingPendingTransactions ||
        isFetchingAccount;

    const isLoading =
        isLoadingTransactions ||
        isLoadingPendingTransactions ||
        isLoadingAccount;

    const transactions: Transaction[] = useMemo(
        () => transactionsData?.pages.flatMap(page => page.items) ?? [],
        [transactionsData],
    );

    const pendingTransactionsCount: number =
        pendingTransactionsData?.totalTransactionsPending;
    const pendingTransactionsAmount: number =
        pendingTransactionsData?.totalAmountPending;

    useEffect(() => {
        refetchTransactions();
    }, [refetchTransactions]);

    const loadMoreTransaction = () => {
        fetchNextPage();
    };

    const formattedTransactions = useMemo(() => getListItems(transactions), [
        transactions,
    ]);

    const items = useMemo<TransactionListElement[]>(() => {
        let transactions: TransactionListElement[] = [...formattedTransactions];

        if (pendingTransactionsCount) {
            const pendingItem: PendingTransactionItem = {
                key: 'pending',
                type: 'pending',
                amount: pendingTransactionsAmount,
                count: pendingTransactionsCount,
                value: pendingTransactionsAmount,
            };

            transactions = [pendingItem, ...transactions];
        }

        if (!isEmpty(account)) {
            const accountBalanceItem: AccountBalanceTransactionItem = {
                key: 'accountBalance',
                type: 'accountBalance',
                amount: account?.availableBalance,
            };

            transactions = [accountBalanceItem, ...transactions];
        }

        return transactions;
    }, [
        formattedTransactions,
        pendingTransactionsCount,
        pendingTransactionsAmount,
        account,
    ]);

    return {
        loadMoreTransaction,
        transactions: items,
        isFetchingTransactions: isFetching,
        isLoadingTransactions: isLoading,
        hasNextPage,
        error,
    };
};

export default useTransactions;
